// Which signaling broker PeerJS should talk to.
//
// Left unconfigured, PeerJS silently falls back to its free public cloud at
// `0.peerjs.com` (util.CLOUD_HOST). That is fine for a hobby demo and wrong for
// a baby monitor: unlike TURN, signaling has no graceful degradation. When the
// broker is unreachable there is no pairing and no reconnect, and PeerJS reports
// the failure as `socket-error` / `server-error`, which app.js treats as fatal
// and paints over the whole UI.
//
// So we always point at our own broker (turn-worker/src/signal.js). Resolution
// mirrors ice.js: explicit override > <meta signal-url> > local dev default >
// same-origin, which is the production case since the Pages Function is
// co-hosted with the app.

function fromUrl(urlString) {
  const url = new URL(urlString);
  const secure = url.protocol === 'https:' || url.protocol === 'wss:';
  // PeerJS builds `host + ":" + port` unconditionally, so an explicit port is
  // required even when it's the scheme default.
  const port = url.port ? Number(url.port) : secure ? 443 : 80;
  // It also expects a directory-style path: `<path>peerjs` for the socket and
  // `<path><key>/id` for the id endpoint.
  let path = url.pathname || '/';
  if (!path.endsWith('/')) path += '/';
  return { host: url.hostname, port, path, secure };
}

// PeerJS options describing our broker. Spread into the Peer constructor.
export function signalOptions() {
  if (window.PEEKABABY_SIGNAL_URL) return fromUrl(window.PEEKABABY_SIGNAL_URL);

  const meta = document.querySelector('meta[name="signal-url"]');
  const fromMeta = meta && meta.content && meta.content.trim();
  if (fromMeta) return fromUrl(fromMeta);

  // Dev convenience, matching ice.js/push.js: `npm run dev` in turn-worker/
  // serves the broker on :8787, so a locally-served app needs no extra config.
  const { hostname, protocol, port } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { host: hostname, port: 8787, path: '/', secure: false };
  }

  // Production: same origin as the app (functions/peerjs/).
  return {
    host: hostname,
    port: port ? Number(port) : protocol === 'https:' ? 443 : 80,
    path: '/',
    secure: protocol === 'https:',
  };
}
